import type React from 'react';

import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core-react/i18n';

type Props = {
	isRecording: boolean;
	disabled: boolean;
	onClick: () => void;
};

/**
 * The camera control, straddling the bottom edge of the preview.
 *
 * A bare button rather than a design-system `Button`: this is a 79px shutter with a red body and a
 * white ring, and there is no `Button` variant that is a circle of that size. It carries its own focus
 * ring for the same reason.
 *
 * Deliberately identical before and after permission is granted. The supporter's cue that the camera is
 * live is the montage giving way to their own face, not a change of control.
 */
export const RecordButton: React.FC<Props> = ({ isRecording, disabled, onClick }) => {
	const { translate } = useI18n();

	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			aria-pressed={isRecording}
			// Keys stay literal at the call site so `no-invalid-translation-keys` can still check them.
			aria-label={
				isRecording
					? translate('corgi.petition_actions.supporter_voice_video.record.record_button.label_stop')
					: translate('corgi.petition_actions.supporter_voice_video.record.record_button.label_start')
			}
			data-testid="consolidated-record"
			data-qa="consolidated-record"
			className="focus-visible:outline-stroke-strong flex h-[79px] w-[79px] cursor-pointer items-center justify-center rounded-full border-0 bg-white p-0 focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
		>
			<span className="bg-fill-brand-strong flex h-[66px] w-[66px] items-center justify-center rounded-full">
				<span
					className={cn(
						'bg-white transition-all duration-200 motion-reduce:transition-none',
						isRecording ? 'h-[22px] w-[22px] rounded-[3px]' : 'h-4 w-4 rounded-full',
					)}
				/>
			</span>
		</button>
	);
};
